import { apiClient as api } from "./httpClient";

export interface ServiceTypeItem {
  id: string;
  description?: string;
  freeHour?: boolean;
  packageAmount?: number;
  professionalType?: string;
  entity?: string;
  service?: string;
  visibleOnSchedule?: boolean;
  isActive: boolean;
  slug?: string;
  createdAt: string;
  updatedAt?: string;
  inUse: boolean; // referenciado em professional_services_values -> não pode excluir
  attendanceKind?: AttendanceKind;
  /** Falso = fora do volume de atendimentos/indicadores (reserva de parceiro, aula por mensalidade) */
  chargePerSession: boolean;
  origin: ServiceOrigin;
  healthPlanId?: string | null;
  healthPlanName?: string | null;
  tableCode?: string | null;
  procedureCode?: string | null;
  /** Como o pacote cobra as sessões da série (RN-227); só importa quando packageAmount > 1 */
  packageChargeMode?: PackageChargeMode;
}

// RN-227: pacote vendido fechado cobra toda sessão da série (o operacional decide na tela) ou só as realizadas
export type PackageChargeMode = "total" | "attended";

export const PACKAGE_CHARGE_MODE_LABELS: Record<PackageChargeMode, string> = {
  total: "Cobrar o pacote inteiro",
  attended: "Cobrar só as sessões realizadas",
};

// De onde o serviço vem (RN-221): o que a clínica sempre teve, ou o procedimento que a operadora
// autorizou no credenciamento, com plano, tabela e código próprios.
export type ServiceOrigin = "own" | "operator";

export const SERVICE_ORIGIN_LABELS: Record<ServiceOrigin, string> = {
  own: "Próprio",
  operator: "Convênio",
};

/** "22" é a tabela de procedimentos da ANS; "00" é tabela própria da operadora (TISS). */
export const PROCEDURE_TABLE_OPTIONS: { code: string; label: string }[] = [
  { code: "22", label: "22 - Procedimentos (TUSS/ANS)" },
  { code: "00", label: "00 - Tabela própria da operadora" },
];

export interface ServiceTypeHealthPlanOption {
  id: string;
  /** "AMIL - 400 A 900": o nome completo, usado quando o plano aparece sozinho. */
  name: string;
  operatorId: string;
  operatorName: string;
}

export interface ImportOutcome {
  created: number;
  updated: number;
  unchanged?: number;
  skipped: number;
  issues: { row: number; reason: string; procedure?: string; professional?: string }[];
}

export interface ProcedureCodeCheck {
  code: string;
  found: boolean;
  displayName: string | null;
  inForce: boolean;
}

/**
 * Como o serviço aparece em combo de outra tela (valores por profissional, por exemplo). O de
 * convênio precisa do plano NA FRENTE (item 177, decisão do Hilton em 25/09/2026 — o mesmo
 * formato que a API compõe nas opções da agenda): "AMIL 400 ao 6500 - Consulta Inicial
 * Psicologia". "Sessão de fonoaudiologia" repetido três vezes, um por plano, seria indistinguível.
 */
export const serviceTypeLabel = (item: {
  description?: string;
  origin?: ServiceOrigin;
  healthPlanName?: string | null;
}): string => {
  const name = item.description || "(sem nome)";
  return item.origin === "operator" && item.healthPlanName
    ? `${item.healthPlanName} - ${name}`
    : name;
};

// Natureza do atendimento: tratamento que se repete x atendimento que termina em si (avaliação,
// avulso, reserva de agenda). Serviço pontual não gera alerta de risco de evasão.
export type AttendanceKind = "continuous" | "punctual";

export const ATTENDANCE_KIND_LABELS: Record<AttendanceKind, string> = {
  continuous: "Continuado",
  punctual: "Pontual",
};

export interface PagedResult<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface ServiceTypeListParams {
  entityId: string;
  professionalType?: string;
  active?: boolean; // undefined = todos; true = ativos; false = inativos
  origin?: ServiceOrigin; // undefined = próprios e de convênio
  pageNumber: number;
  pageSize: number;
}

export interface ServiceTypePayload {
  description?: string;
  freeHour?: boolean;
  packageAmount?: number;
  professionalType?: string;
  entity?: string;
  service?: string;
  visibleOnSchedule?: boolean;
  slug?: string;
  attendanceKind?: AttendanceKind;
  chargePerSession?: boolean;
  origin?: ServiceOrigin;
  healthPlanId?: string | null;
  tableCode?: string | null;
  procedureCode?: string | null;
  packageChargeMode?: PackageChargeMode;
}

export const serviceTypeService = {
  getByEntity: async (
    params: ServiceTypeListParams
  ): Promise<PagedResult<ServiceTypeItem>> => {
    const response = await api.get("/service-types", {
      params: {
        EntityId: params.entityId,
        ProfessionalType: params.professionalType || undefined,
        Active: params.active,
        Origin: params.origin || undefined,
        PageNumber: params.pageNumber,
        PageSize: params.pageSize,
      },
    });
    return response.data;
  },
  getHealthPlanOptions: async (): Promise<ServiceTypeHealthPlanOption[]> => {
    const response = await api.get("/service-types/health-plan-options");
    return response.data;
  },
  importTemplate: async (): Promise<Blob> => {
    const response = await api.get("/service-types/import-template", { responseType: "blob" });
    return response.data;
  },
  /** dryRun = prévia (nada é gravado); false aplica. */
  importSheet: async (file: File, dryRun: boolean, updateExisting = true): Promise<ImportOutcome> => {
    const body = new FormData();
    body.append("file", file);
    body.append("dryRun", String(dryRun));
    body.append("updateExisting", String(updateExisting));
    const response = await api.post("/service-types/import", body, {
      headers: { "Content-Type": null },
    });
    return response.data;
  },
  checkProcedureCode: async (code: string): Promise<ProcedureCodeCheck> => {
    const response = await api.get(`/service-types/procedure-code/${code}`);
    return response.data;
  },
  create: async (payload: ServiceTypePayload): Promise<void> => {
    await api.post("/service-types", payload);
  },
  update: async (
    id: string,
    payload: ServiceTypePayload & { active: boolean }
  ): Promise<void> => {
    await api.put(`/service-types/${id}`, payload);
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/service-types/${id}`);
  },
};
